import React, { useCallback, useContext, useEffect, useState } from "react";
import { WorkoutContext } from "../contexts/WorkoutContext";
import EmptyState from "../components/session/EmptyState";
import ActiveState from "../components/session/ActiveState";
import GenerateSessionDialog from "../components/session/GenerateSessionDialog";
import LoadSessionDialog from "../components/session/LoadSessionDialog";
import { consumeStartToday } from "../utils/setupFlow";

const SessionView = ({ onNavigate }) => {
  const { workoutPlan } = useContext(WorkoutContext);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  // A recipe picked from the empty state (issue #78). Held as an id — the
  // dialog resolves and applies it, so this view stays out of recipe shape.
  const [generateRecipeId, setGenerateRecipeId] = useState(null);
  // Today's suggestion: the shape it showed, and whether Start (build it now)
  // or Customise (open the form filled in) was pressed.
  const [suggestion, setSuggestion] = useState(null);

  const openGenerate = useCallback(() => {
    setGenerateRecipeId(null);
    setSuggestion(null);
    setGenerateOpen(true);
  }, []);

  const openGenerateWithRecipe = useCallback((recipeId) => {
    setGenerateRecipeId(recipeId);
    setSuggestion(null);
    setGenerateOpen(true);
  }, []);

  const startSuggestion = useCallback((overrides, { autoGenerate } = {}) => {
    setGenerateRecipeId(null);
    setSuggestion({ overrides, autoGenerate: Boolean(autoGenerate) });
    setGenerateOpen(true);
  }, []);

  // A Start or Customise handed over from elsewhere (getting started, the
  // Coach at rest). With the shape included it's acted on here, whatever this
  // tab is showing; without one, the empty state works today's out.
  const [startRequest, setStartRequest] = useState(null);
  useEffect(() => {
    const request = consumeStartToday();
    if (!request) return;
    if (request.overrides) {
      startSuggestion(request.overrides, { autoGenerate: request.action === "start" });
    } else {
      setStartRequest(request);
    }
  }, [startSuggestion]);

  return (
    <>
      {workoutPlan ? (
        <ActiveState onOpenGenerate={openGenerate} />
      ) : (
        <EmptyState
          onOpenGenerate={openGenerate}
          onOpenGenerateWithRecipe={openGenerateWithRecipe}
          onOpenLoad={() => setLoadOpen(true)}
          onStartSuggestion={startSuggestion}
          onNavigate={onNavigate}
          startRequest={startRequest}
          onStartRequestHandled={() => setStartRequest(null)}
        />
      )}

      <GenerateSessionDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        onNavigate={onNavigate}
        initialRecipeId={generateRecipeId}
        configOverrides={suggestion?.overrides}
        prefillLabel="today's suggestion"
        autoGenerate={suggestion?.autoGenerate}
      />
      <LoadSessionDialog open={loadOpen} onOpenChange={setLoadOpen} />
    </>
  );
};

export default SessionView;
